/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdaptiveFormShell } from '@/shared/ui/components/form';
import { SearchAutocomplete } from '@/shared/ui/components/form/SearchAutocomplete';
import { SegmentedControl, Text, TextInput } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { moneyInputMask, moneyMask } from '@/shared/utils/input-masks';
import { DealDrawerSubmitButton } from './DealDrawerSubmitButton';
import { ProposalTermsFields, omitEmptyTerms } from './ProposalTermsFields';
import type {
  RentalTerms,
  SaleTerms,
} from '@/services/requests/housien-services/DealsService';
import { DateField } from '@/shared/ui/components/form/inputs';

/** What the dialog hands back, shaped as the endpoints want it. */
export interface ProposalSubmission {
  deal_type: 'sale' | 'rental';
  rental_terms?: RentalTerms;
  sale_terms?: SaleTerms;
  valid_until?: string;
  /**
   * The property this offer is about.
   *
   * Sent explicitly rather than left to the server's fallback to the
   * attendance's primary. An attendance follows a client and may list
   * several properties -- someone who visits a place and dislikes it
   * wants to see another, and that is the same piece of work -- so
   * every offer used to land on the primary no matter which property
   * the broker had actually offered on.
   */
  property_id?: string;
}

/** One property the offer may be about, as the picker shows it. */
export interface ProposalPropertyOption {
  id: string;
  label: string;
}

interface RecordProposalDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (value: number, body: ProposalSubmission) => Promise<void>;
  /**
   * The offer being answered, when this is a counter. Pre-fills the
   * form with what the other side said, because a counter is an answer
   * to terms, not a blank page.
   */
  counterTo?: {
    value: number;
    deal_type: 'sale' | 'rental';
    rental_terms?: RentalTerms | null;
    sale_terms?: SaleTerms | null;
    property_id?: string;
  };
  /** Which kind to start on when this is a fresh offer. */
  defaultDealType?: 'sale' | 'rental';
  /**
   * The type the attendance already turned out to be, if any.
   *
   * When set, the choice is not offered: the attendance's type *is*
   * what its offers said, so a second offer of the other kind would
   * contradict it -- and two offers disagreeing with each other leave
   * `deal_type` permanently uncorrectable, since every value then has
   * an offer against it.
   */
  lockedDealType?: 'sale' | 'rental' | null;
  /**
   * The attendance's properties of interest, primary first. The picker
   * appears from two upwards: with one there is nothing to choose, and
   * with none the panel does not open this dialog at all.
   */
  properties?: readonly ProposalPropertyOption[];
  /**
   * What this dialog is recording.
   *
   * `win` is the same form under different copy: a close agreed on the
   * phone *is* an accepted offer, it simply was never written down. The
   * caller records it and accepts it in one step, so the attendance is
   * won on a real offer with a value, a property and a date -- rather
   * than on a `closed_won` with nothing behind it, which would open a
   * Venda or a Locação with no number in it.
   */
  mode?: 'offer' | 'win';
  /**
   * Whether the first option is the attendance's own property.
   *
   * False when the attendance has none and the list is the agency's
   * whole catalogue: preselecting there would attach an arbitrary
   * property to the closing without anyone choosing it.
   */
  preselectProperty?: boolean;
}

const labelClasses = cn(
  'text-[length:var(--text-meta)]',
  'font-[weight:var(--font-medium)]',
  'text-(--text-secondary)',
);

/**
 * Dialog recording the offer put in front of the other side.
 *
 * Recording is what moves the deal to the proposal stage — there is no
 * separate Advance for it, because the stage is a consequence of the
 * offer existing, not a step someone marks as done.
 *
 * Terms are optional throughout. The broker who has only a number has
 * to be able to record it; asking for the whole arrangement before
 * accepting anything is how offers end up living in WhatsApp.
 *
 * @param props - Open state, callbacks, and the offer being countered.
 * @returns The record-proposal form shell.
 */
export function RecordProposalDialog({
  open, onClose, onSubmit, counterTo, defaultDealType = 'sale',
  properties = [], lockedDealType = null, mode = 'offer',
  preselectProperty = true,
}: RecordProposalDialogProps) {
  const { t } = useTranslation('features/deals');
  // Holds moneyMask's unmasked `toFixed(2)` numeric string; the input
  // always displays the masked ("R$ 0,00") form of it.
  const [value, setValue] = useState('');
  const [dealType, setDealType] = useState<'sale' | 'rental'>(defaultDealType);
  const [rentalTerms, setRentalTerms] = useState<RentalTerms>({});
  const [saleTerms, setSaleTerms] = useState<SaleTerms>({});
  const [validUntil, setValidUntil] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // A counter starts from what it answers: same type, same terms, and
  // the previous number as the thing being argued with.
  useEffect(() => {
    if (!open) return;
    setDealType(counterTo?.deal_type ?? lockedDealType ?? defaultDealType);
    setRentalTerms(counterTo?.rental_terms ?? {});
    setSaleTerms(counterTo?.sale_terms ?? {});
    setValue(counterTo ? counterTo.value.toFixed(2) : '');
    setValidUntil('');
    // A counter is about the property it answers; a fresh offer starts
    // on the primary, which is the attendance's own first entry.
    setPropertyId(
      counterTo?.property_id ?? (preselectProperty ? properties[0]?.id ?? '' : ''),
    );
    setTouched(false);
  }, [open, counterTo, defaultDealType, lockedDealType, properties, preselectProperty]);

  // The picker earns its place when there is a choice to make, and when
  // nothing was preselected for the broker.
  const choosingProperty =
    !counterTo && properties.length > 0
    && (properties.length > 1 || !preselectProperty);
  // A win opens a Venda or Locação against a property. The server
  // refuses one without, but only after the form has been filled in and
  // sent -- and on an attendance carrying no property the picker was not
  // even drawn, so the closing was unreachable with nothing said.
  const missingProperty = mode === 'win' && choosingProperty && !propertyId;
  const canSubmit = Number(value) > 0 && !missingProperty;
  const showError = touched && Number(value) <= 0;

  const handleSubmit = async () => {
    setTouched(true);
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onSubmit(Number(value), {
        deal_type: dealType,
        ...(dealType === 'rental'
          ? { rental_terms: omitEmptyTerms(rentalTerms) }
          : { sale_terms: omitEmptyTerms(saleTerms) }),
        ...(validUntil ? { valid_until: validUntil } : {}),
        ...(propertyId ? { property_id: propertyId } : {}),
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const prefix = counterTo
    ? 'counterDialog'
    : (mode === 'win' ? 'winDialog' : 'proposalDialog');

  return (
    <AdaptiveFormShell
      open={open}
      onClose={onClose}
      title={t(`detail.${prefix}.title`)}
      density="compact"
      footer={
        <DealDrawerSubmitButton
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
          label={t(`detail.${prefix}.confirm`)}
          savingLabel={t(`detail.${prefix}.confirming`)}
        />
      }
    >
      <div data-testid="record-proposal-form" className="flex flex-col gap-(--space-4)">
        {/* First, and centred: it decides which terms the rest of the
            form shows, so asking anything else before it is asking a
            question whose fields might be about to be replaced.

            A counter keeps the kind of the offer it answers -- the two
            sides are arguing over one arrangement, not two -- so the
            choice is not offered at all there. */}
        {!counterTo && lockedDealType && (
          <fieldset className="flex flex-col items-center gap-(--space-1) border-none p-0 m-0">
            <Text as="label" className={labelClasses}>{t('detail.proposalDialog.type')}</Text>
            <Text
              component="span"
              data-testid="proposal-type-locked"
              className="text-[length:var(--text-body-sm)] text-(--text-primary)"
            >
              {t(`types.${lockedDealType}`)}
            </Text>
          </fieldset>
        )}

        {!counterTo && !lockedDealType && (
          <fieldset className="flex flex-col items-center gap-(--space-1) border-none p-0 m-0">
            <Text as="label" className={labelClasses}>{t('detail.proposalDialog.type')}</Text>
            <SegmentedControl
              value={dealType}
              onChange={(v) => setDealType(v as 'sale' | 'rental')}
              items={[
                { value: 'sale', label: t('sale') },
                { value: 'rental', label: t('rental') },
              ]}
              size="md"
              testId="proposal-type"
              // The control hardcodes `self-start`, which wins over the
              // fieldset's items-center unless overridden here.
              className="self-center"
            />
          </fieldset>
        )}

        {/* Only from two upwards, and never on a counter: a counter
            argues over the property already on the table. */}
        {choosingProperty && (
          <fieldset className="flex flex-col gap-(--space-1) border-none p-0 m-0">
            <Text as="label" className={labelClasses}>
              {t('detail.proposalDialog.property')}
            </Text>
            <SearchAutocomplete
              options={properties as ProposalPropertyOption[]}
              value={propertyId || null}
              onChange={(id) => setPropertyId(id ?? '')}
              placeholder={t('detail.proposalDialog.propertyPlaceholder')}
            />
            {touched && missingProperty && (
              <Text
                component="span"
                data-testid="proposal-property-error"
                className="text-[length:var(--text-meta)] text-(--status-error-text)"
              >
                {t('detail.proposalDialog.propertyError')}
              </Text>
            )}
            <Text
              component="span"
              className="text-[length:var(--text-meta)] leading-normal text-(--text-tertiary)"
            >
              {t('detail.proposalDialog.propertyHint')}
            </Text>
          </fieldset>
        )}

        <fieldset className="flex flex-col gap-(--space-1) border-none p-0 m-0">
          {/* A closing is not a proposal: by the time this form opens
              the two sides have agreed, so "Valor proposto" described a
              negotiation that had already ended. */}
          <Text as="label" htmlFor="proposal-value" className={labelClasses}>
            {t(`detail.${mode === 'win' ? 'winDialog' : 'proposalDialog'}.value`)}
          </Text>
          <TextInput
            id="proposal-value"
            data-testid="proposal-value-input"
            inputMode="decimal"
            value={value ? moneyMask(value).masked : ''}
            onChange={(e) => setValue(moneyInputMask(e.target.value).unmasked)}
            onBlur={() => setTouched(true)}
            error={showError ? t('detail.proposalDialog.valueError') : undefined}
            placeholder={t('detail.proposalDialog.valuePlaceholder')}
            autoFocus
          />
        </fieldset>

        <ProposalTermsFields
          dealType={dealType}
          rentalTerms={rentalTerms}
          saleTerms={saleTerms}
          onRentalChange={setRentalTerms}
          onSaleChange={setSaleTerms}
        />

        {/* Informational only. Nothing expires on its own — a date that
            passed is a note the broker left themselves, not a status.
            Absent on a win: the offer being recorded is one the other
            side already accepted, so a deadline for answering it is a
            question nobody is waiting on. */}
        {mode !== 'win' && (
        <fieldset className="flex flex-col gap-(--space-1) border-none p-0 m-0">
          <Text as="label" htmlFor="proposal-valid-until" className={labelClasses}>
            {t('terms.validUntil')}
          </Text>
          <DateField
            id="proposal-valid-until"
            data-testid="proposal-valid-until"
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
          />
        </fieldset>
        )}

        <Text component="span" className="text-[length:var(--text-meta)] text-(--text-tertiary)">
          {t(`detail.${prefix === 'counterDialog' ? 'proposalDialog' : prefix}.hint`)}
        </Text>
      </div>
    </AdaptiveFormShell>
  );
}

export default RecordProposalDialog;
